import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../i18n';
import { X, ArrowDownCircle } from 'lucide-react';

const OP_TYPES = [
  { value: 'depense',       emoji: '🛒' },
  { value: 'retrait',       emoji: '💸' },
  { value: 'remboursement', emoji: '↩️' },
  { value: 'autre',         emoji: '📌' },
];

export default function ModalOperation({ open, onClose, onSave, initial, devise }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ libelle: '', montant: '', type: 'depense', date: '', note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().split('T')[0];
      setForm(initial ? {
        libelle: initial.libelle || '',
        montant: initial.montant || '',
        type: initial.type || 'depense',
        date: initial.date || today,
        note: initial.note || '',
      } : { libelle: '', montant: '', type: 'depense', date: today, note: '' });
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const typeLabel = (v) => {
    const map = {
      depense: t.cagnotte.typeDepense,
      retrait: t.cagnotte.typeRetrait,
      remboursement: t.cagnotte.typeRemboursement,
      autre: t.cagnotte.typeAutre,
    };
    return map[v] || v;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.libelle || !form.montant) return;
    setSaving(true);
    try {
      await onSave({ ...form, montant: Number(form.montant) });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 460, width: '95%' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ArrowDownCircle size={18} style={{ color: 'var(--danger)' }} />
            <h2 className="modal-title">{initial ? 'Modifier l\'opération' : t.cagnotte.logOperation}</h2>
          </div>
          <button className="modal-close" onClick={onClose} type="button"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* Type selector */}
            <div className="form-group">
              <label className="form-label">{t.cagnotte.operationType} <span className="required">*</span></label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {OP_TYPES.map(({ value, emoji }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, type: value }))}
                    style={{
                      padding: '7px 13px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      border: `2px solid ${form.type === value ? 'var(--primary)' : 'var(--border)'}`,
                      background: form.type === value ? 'rgba(var(--primary-rgb, 16,185,129), 0.1)' : 'var(--bg-secondary)',
                      color: form.type === value ? 'var(--primary)' : 'var(--text-secondary)',
                      fontWeight: form.type === value ? 700 : 400,
                      transition: 'all 0.15s',
                    }}
                  >
                    {emoji} {typeLabel(value)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t.cagnotte.operationLibelle} <span className="required">*</span></label>
              <input
                id="op-libelle"
                className="form-control"
                value={form.libelle}
                onChange={set('libelle')}
                required
                placeholder="Ex: Achat matériel, Loyer..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t.cagnotte.operationMontant} ({devise || 'FCFA'}) <span className="required">*</span></label>
                <input
                  id="op-montant"
                  type="number"
                  min="0"
                  step="any"
                  className="form-control"
                  value={form.montant}
                  onChange={set('montant')}
                  required
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.cagnotte.operationDate}</label>
                <input id="op-date" type="date" className="form-control" value={form.date} onChange={set('date')} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t.cagnotte.operationNote}</label>
              <textarea
                id="op-note"
                className="form-control"
                value={form.note}
                onChange={set('note')}
                rows={2}
                placeholder="Note optionnelle..."
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !form.libelle || !form.montant}>
              {saving ? t.common.loading : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
